
import { Component, Vue } from 'vue-property-decorator'
import AdminRoleForm from '@/components/admin/AdminRoleForm.vue'
import { MessageError } from '@/components/generic/MessageHandling.vue'
import TopToolBarPaginator from '@/components/generic/TopToolBarPaginator.vue'
import catalogsService from '@/services/CatalogsService'
import { CrudActions, RoleCatalogFilters, RoleCatalogGridItem, Role, CatalogGridItemColumn, DomainCatalogFilters, Domain, BasicEntity } from '@/types/Catalogs'
import { PageRequest } from '@/types/Support'
import userInfoStore from '@/store/userInfo'
import { PermissionNames } from '@/types/Auth'
import DateFormat from '@/services/DateFormat'
import LogService, { ToString } from '@/services/support/LogService'

@Component({
  components: {
    AdminRoleForm,
    MessageError,
    TopToolBarPaginator
  }
})
export default class AdminRoles extends Vue {
  PAGE_SIZE = 500
  PAGE_SIZE_VISIBLE_ITEMS = 50
  itemsVisibleRange = [0, this.PAGE_SIZE_VISIBLE_ITEMS]
  paginatorKey = 0
  crudActions = CrudActions
  currentAction = CrudActions.READ
  loadingData = false
  leftSidebarCollapsed = false
  roleColumns = [
    CatalogGridItemColumn.def('id', 'ID', true),
    CatalogGridItemColumn.def('domain', 'Dominio', true),
    CatalogGridItemColumn.def('name', 'Nombre', true),
    CatalogGridItemColumn.def('maxSessions', 'Sesiones máximas', true),
    CatalogGridItemColumn.def('copyFromRole', 'Copiado del Rol', true),
    CatalogGridItemColumn.def('creation', 'Fecha de creación', true),
    CatalogGridItemColumn.def('update', 'Fecha de actualización', true)
  ]
  totalItems: number = 0
  items: RoleCatalogGridItem[] = []
  filteredItems: RoleCatalogGridItem[] = []
  filters = new RoleCatalogFilters()
  selectedItem = new RoleCatalogGridItem()
  searchBy = ''

  async created () {
    this.updateData()
    if (this.canFilterByDomain) {
      await this.loadDomains(new PageRequest<DomainCatalogFilters>(this.PAGE_SIZE))
    }
  }

  /* User permissions */

  /**
   * Check if user has 'permission' permission
   * @param permission name to check
   */
  hasPermission (permision: string) {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === permision)
  }

  /**
   * Checks if the user can view domain filter, for this
   * the user must belongs to root domain and has domains read permission.
   */
  get canFilterByDomain () {
    return userInfoStore.inRootDomain() && this.hasPermission(PermissionNames.DOMAINS_READ)
  }

  /**
   * Checks what records user can update
   */
  get canUpdate () {
    return this.selectedItem.id && this.hasPermission(PermissionNames.ROLES_UPDATE)
  }

  /**
   * Checks what records user can delete
   */
  get canDelete () {
    return this.selectedItem.id && this.hasPermission(PermissionNames.ROLES_DELETE)
  }

  /**
   * Clear and update all permission records
   */
  async updateData () {
    if (this.loadingData) return
    this.loadingData = true
    this.items = []
    const pageRequest = new PageRequest<RoleCatalogFilters>(this.PAGE_SIZE)
    pageRequest.filters = this.filters
    this.loadPage(pageRequest)
  }

  /**
   * Get all role records and assign them in 'items' variable
   * @param pageRequest - configurations to request
   */
  loadPage (pageRequest: PageRequest<any>) {
    catalogsService.getRoles(pageRequest).then(response => {
      this.items.push(...response.results.map((it: Role): RoleCatalogGridItem => {
        return this.fillGridItem(it, new RoleCatalogGridItem())
      }))
      if (this.items.length < response.total) {
        pageRequest.page++
        this.loadPage(pageRequest)
      } else {
        this.loadingData = false
        this.filteredItems = this.items
        this.totalItems = this.filteredItems.length
      }
    }).catch((error) => {
      LogService.error(`Error en la carga de roles: ${ToString(error.response ?? error)}`)
      this.loadingData = false
    })
  }

  /**
   * Gets all domains records and assing them in 'domains' variable
   * @param pageRequest - request configurations
   */
  async loadDomains (pageRequest: PageRequest<any>) {
    this.loadingDomains = true
    catalogsService.getDomains(pageRequest).then((response) => {
      this.domains.push(...response.results.map((it: Domain) => {
        return { id: it.id, name: it.name } as BasicEntity
      }))
      if (this.domains.length < response.total) {
        pageRequest.page++
        this.loadDomains(pageRequest)
      }
    }).finally(() => {
      this.loadingDomains = false
      this.selectDefaultDomain()
    })
  }

  /**
   * Create a RoleCatagolGridItem with base on Role
   * @param origin - to obtains values
   * @param target - RoleCatalogGridItem to fill with origin object values
   */
  fillGridItem (origin: Role, target: RoleCatalogGridItem) {
    target.id = origin.id
    target.copyFromRole = origin.copyFromRole
    target.maxSessions = origin.maxSessions
    target.name = origin.name
    target.domain = origin.domain
    target.creation = origin.creationDate ? new DateFormat(origin.creationDate).toDateString() : '- - / - - / - - - -'
    target.update = origin.updateDate ? new DateFormat(origin.updateDate).toDateString() : '- - / - - / - - - -'
    return target
  }

  /**
   * Select the current item on double clic.
   * @param item - row selected on table
   */
  selectItemAndShowDetail (item: any) {
    this.currentAction = CrudActions.READ_DETAIL
    this.selectItem(item)
  }

  /**
   * Select the current item and assign it in 'selectedItem' variable.
   * @param item - row selected on table
   */
  selectItem (item: any) {
    switch (this.currentAction) {
      case CrudActions.READ:
      case CrudActions.READ_DETAIL:
      case CrudActions.UPDATE:
      case CrudActions.DELETE:
        this.selectedItem = item
        break
      case CrudActions.CREATE:
        this.selectedItem = this.hasPermission(PermissionNames.ROLES_UPDATE) ? item : this.selectedItem
        this.currentAction = CrudActions.UPDATE
        break
    }
  }

  /**
   * Check if 'item' is the current item selected
   * @param item - row table
   */
  isItemSelected (item: any) {
    return this.selectedItem === item
  }

  /** Clear the current selected item */
  cleanItemSelected () {
    this.selectedItem = new RoleCatalogGridItem()
  }

  /**
   * This triggers a form view transition event to read role
   */
  queryItem () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.READ_DETAIL
    }
  }

  /**
   * This triggers a form view transition event to create role
   */
  newItem () {
    this.selectedItem = new RoleCatalogGridItem()
    this.currentAction = CrudActions.CREATE
  }

  /**
   * This triggers a form view transition event to update role
   */
  updateItem () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.UPDATE
    }
  }

  /**
   * Delete the current role selected after a confirmation message.
   */
  deleteItem () {
    if (this.selectedItem.id) {
      let messageConfirmation = `Esta por eliminar el registro:\
        "${this.selectedItem.id} - ${this.selectedItem.name}".\
        Esta acción es irreversible y se perderá permanentemente dicho registro.\
        ¿Estás seguro de continuar?`
      this.$bvModal.msgBoxConfirm(messageConfirmation, {
        title: 'Eliminando rol',
        size: 'md',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'Confirmar',
        cancelVariant: 'outline-secondary',
        cancelTitle: 'Cancelar',
        headerClass: 'p-2 border-bottom-0',
        footerClass: 'p-2 border-top-0',
        hideHeaderClose: false
      }).then(value => {
        if (value) {
          catalogsService.deleteRoleById(this.selectedItem.id).then((response) => {
            this.$bvToast.toast(`El rol ${this.selectedItem.id} se ha eliminado correctamente`, {
              title: 'Eliminación finalizada',
              variant: 'success',
              toaster: 'b-toaster-top-center',
              autoHideDelay: 3000
            })
          }).catch((error) => {
            let messageValues = {
              title: 'Error al eliminar el rol.',
              message: '',
              delay: 10000,
              error: error
            }
            let errorComponent = new MessageError({ propsData: messageValues })
            errorComponent.displayMessage()
          }).finally(() => {
            this.updateData()
            this.currentAction = CrudActions.READ
          })
        }
      })
    }
  }

  /**
   * Returns the current number of filtered items
   */
  get countTotalItems () {
    return this.filteredItems.length || this.items.length
  }

  /* Domain selector */

  domainSelectionOpen = false
  domainIdSelected = -1
  domainSelected: any = {}
  domainSelectedBackup: any = {}
  searchDomain = ''
  domains: BasicEntity[] = []
  loadingDomains = false

  /**
   * Active domainSelectionOpen flag to display the dropdown domain list
   * and updates the searchDomain and domainSelected values when the dropdown is close
   * @param isOpen flag
   */
  showDomainSelection (isOpen: boolean) {
    if (isOpen) {
      this.searchDomain = ''
      this.domainSelected = {}
      this.domainSelectionOpen = true
    } else {
      setTimeout(() => {
        this.domainSelectionOpen = false
        if (this.domainSelectedBackup.name && this.domainSelectedBackup.id !== this.domainSelected.id) {
          this.searchDomain = this.domainSelectedBackup.name
          this.domainSelected = this.domainSelectedBackup
        }
      }, 200)
    }
  }

  /**
   * Validates if a domain name matches with the current searchDomain value
   * @param domain entity to check its name
   */
  isMatchDomain (domain: BasicEntity) {
    if (!this.searchDomain) return true
    const regexSearch = new RegExp(this.searchDomain, 'i')
    return regexSearch.test(domain.name)
  }

  /**
   * Updates domains variables based on 'domain' selected
   * @param domain selected
   */
  selectDomain (domain: BasicEntity) {
    this.domainSelected = domain
    this.domainSelectedBackup = domain
    this.searchDomain = domain.name
    this.domainIdSelected = domain.id
  }

  /**
   * Sets the current user domain as default domain selected
   */
  selectDefaultDomain () {
    this.filters.domain = userInfoStore.userInfo.domain.id
    this.domainIdSelected = userInfoStore.userInfo.domain.id
    const domain = this.domains.find(it => it.id === this.domainIdSelected)
    if (domain) this.selectDomain(domain)
  }

  /* Filter and sorting data */

  async filterItems () {
    if (this.filters.domain !== this.domainIdSelected) {
      this.filters.domain = this.domainIdSelected
      await this.updateData()
    }

    const regexSearch = new RegExp(this.searchBy, 'i')
    this.resetPaginator()
    this.filteredItems = this.items.filter(it =>
      (!this.searchBy ||
      regexSearch.test(it.name) ||
      regexSearch.test(it.copyFromRole) ||
      regexSearch.test(it.domain.toString()))
    )
  }

  /**
   * Sort current filtered items with base a specific column
   * @param col - column to filter with
   */
  sortItems (col: CatalogGridItemColumn) {
    catalogsService.sortItems(this.filteredItems, col)
  }

  /** Calculates visible index range for pagination */
  onPageChange (page: number) {
    this.itemsVisibleRange = [(page - 1) * this.PAGE_SIZE_VISIBLE_ITEMS, (page - 1) * this.PAGE_SIZE_VISIBLE_ITEMS + this.PAGE_SIZE_VISIBLE_ITEMS]
    this.cleanItemSelected()
  }

  /** Reset index range pagination and clear current selected item */
  resetPaginator () {
    this.itemsVisibleRange = [0, this.PAGE_SIZE_VISIBLE_ITEMS]
    this.paginatorKey = new Date().getTime()
    this.cleanItemSelected()
  }

  /**
   * Clear current filter configurations
   */
  cleanFilter () {
    this.searchBy = ''
    this.filters = new RoleCatalogFilters()
    this.filteredItems = this.items
    this.totalItems = this.filteredItems.length
  }

  /** Check if the index of a row is within the visible range  */
  isRowVisibleInPage (rowIndex: number) {
    return rowIndex >= this.itemsVisibleRange[0] && rowIndex < this.itemsVisibleRange[1]
  }

  /**
   * Filter funtion to already items
   */
  onFiltered (filteredItems: any) {
    this.totalItems = filteredItems.length
  }

  /* Form */
  /**
   * Switch to edit mode
   */
  changeToEditMode () {
    this.currentAction = CrudActions.UPDATE
  }

  onItemSaved (item: Role) {
    if (!this.selectedItem.id) {
      this.selectedItem = new RoleCatalogGridItem()
      this.selectedItem.id = item.id
      this.items.unshift(this.selectedItem)
    }
    this.fillGridItem(item, this.selectedItem)
    this.currentAction = CrudActions.READ
  }

  onCancelForm () {
    this.currentAction = CrudActions.READ
  }

  /** Export data */
  printCsv () {
    let csv = ''
      .concat('ID').concat(',')
      .concat('DOMAIN').concat(',')
      .concat('NAME').concat(',')
      .concat('MAX SESSIONS').concat(',')
      .concat('COPY FROM ROLE').concat(',')
      .concat('CREACIÓN').concat(',')
      .concat('ACTUALIZACIÓN').concat('\n')

    for (let it of this.filteredItems) {
      csv += ''
        .concat(it.id.toString()).concat(',')
        .concat(it.domain.toString()).concat(',')
        .concat(it.name).concat(',')
        .concat(it.maxSessions.toString()).concat(',')
        .concat(it.copyFromRole).concat(',')
        .concat(it.creation).concat(',')
        .concat(it.update).concat('\n')
    }

    let element = this.$refs.csvDownload as any
    element.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv)
    element.target = '_blank'
    element.download = `Roles.csv`
    element.click()
  }
}
